import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { SpeedometerGauge } from "@/components/SpeedometerGauge";

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("ar", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
};

const PHASE_LABELS = {
  connecting: "جاري الاتصال بالسيرفر...",
  ping: "قياس البينغ 📡",
  download: "قياس التنزيل ⬇️",
  upload: "قياس الرفع ⬆️",
  done: "اكتمل القياس ✔",
  error: "فشل القياس ⚠️",
};

export default function SpeedTestCard() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [live, setLive] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [servers, setServers] = useState([]);
  const [serversSource, setServersSource] = useState(null);
  const [serversLoading, setServersLoading] = useState(false);
  const [serversError, setServersError] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [selected, setSelected] = useState(null); // {id, name, location, country} or null = auto
  const pollRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await api.get("/speedtest/latest", { timeout: 15000 });
      setState(res.data);
      return res.data;
    } catch (e) {
      if (e?.response?.status !== 401) console.error("speedtest fetch error", e);
      return null;
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get("/speedtest/history?limit=8", { timeout: 15000 });
      setHistory(res.data.results || []);
    } catch (e) { /* silent */ }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startLivePolling = useCallback(() => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const res = await api.get("/speedtest/live", { timeout: 8000 });
        setLive(res.data);
        if (!res.data.running || ticks > 200) {
          stopPolling();
          setRunning(false);
          await fetchLatest();
          fetchHistory();
          // keep the final gauge on screen for a moment, then clear
          setTimeout(() => setLive(null), 3500);
        }
      } catch (e) { /* keep polling */ }
    }, 1000);
  }, [stopPolling, fetchLatest, fetchHistory]);

  useEffect(() => {
    (async () => {
      const data = await fetchLatest();
      fetchHistory();
      // If a test is already running (e.g. page reload), resume live view
      if (data?.running) {
        setRunning(true);
        startLivePolling();
      }
    })();
    return () => stopPolling();
  }, [fetchLatest, fetchHistory, startLivePolling, stopPolling]);

  const loadServers = async (query) => {
    setServersLoading(true);
    setServersError(null);
    try {
      const params = query && query.trim() ? `?search=${encodeURIComponent(query.trim())}` : "";
      const res = await api.get(`/speedtest/servers${params}`, { timeout: 45000 });
      setServers(res.data.servers || []);
      setServersSource(res.data.source);
      if ((res.data.servers || []).length === 0) {
        setServersError("لا توجد نتائج — جرّب كلمة بحث أخرى");
      }
    } catch (e) {
      setServersError(e?.response?.data?.detail || "تعذر جلب السيرفرات — حاول مجدداً");
    } finally {
      setServersLoading(false);
    }
  };

  const openPicker = () => {
    const next = !pickerOpen;
    setPickerOpen(next);
    if (next && servers.length === 0) loadServers("");
  };

  const runTest = async () => {
    setRunning(true);
    setLive({ running: true, phase: "connecting", current_mbps: 0, progress: 0 });
    try {
      await api.post("/speedtest/run", { server_id: selected?.id ?? null }, { timeout: 15000 });
    } catch (e) {
      console.error("speedtest run error", e);
      setRunning(false);
      setLive(null);
      return;
    }
    startLivePolling();
  };

  const latest = state?.latest;
  const showGauge = running || (live && (live.phase === "done" || live.phase === "error"));
  const phase = live?.phase || "connecting";
  const gaugeValue = live?.phase === "done" ? 0 : (live?.current_mbps || 0);
  const progressPct = Math.round((live?.progress || 0) * 100);

  return (
    <section className="card speedtest-card" data-testid="speedtest-card">
      <div className="card-head">
        <h2>⚡ قياس سرعة السيرفر</h2>
        <button
          className="btn btn-primary"
          onClick={runTest}
          disabled={running}
          data-testid="speedtest-run-btn"
        >
          {running ? <span className="spinner" /> : "🚀"}{" "}
          {running ? "جاري القياس..." : "ابدأ القياس"}
        </button>
      </div>
      <p className="tickets-sub">
        قياس حقيقي يعمل على سيرفرك مباشرة عبر Speedtest CLI الرسمي من Ookla — ببث لحظي مباشر
      </p>

      {/* ===== Server picker (manual selection like the mobile app) ===== */}
      <div className="server-picker-row" data-testid="server-picker-row">
        <span className="server-picker-label">🖥 سيرفر القياس:</span>
        <span className={`server-picker-chip ${selected ? "manual" : "auto"}`} data-testid="server-picker-chip">
          {selected
            ? `${selected.name} — ${selected.location || ""} (ID ${selected.id})`
            : "تلقائي (الأفضل تلقائياً)"}
        </span>
        {selected && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setSelected(null)}
            data-testid="server-picker-reset-btn"
          >
            ↩ تلقائي
          </button>
        )}
        <button
          className="btn btn-ghost btn-sm"
          onClick={openPicker}
          data-testid="server-picker-toggle-btn"
        >
          {pickerOpen ? "إغلاق القائمة ▲" : "اختيار سيرفر ▼"}
        </button>
      </div>

      {pickerOpen && (
        <div className="server-picker-panel" data-testid="server-picker-panel">
          <div className="server-picker-search">
            <input
              type="text"
              placeholder="ابحث عالمياً: دولة / مدينة / اسم مزوّد... (مثال: dubai, MTN, turkey)"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadServers(searchQ); }}
              data-testid="server-search-input"
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={() => loadServers(searchQ)}
              disabled={serversLoading}
              data-testid="server-search-btn"
            >
              {serversLoading ? <span className="spinner" /> : "🔍"} بحث
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setSearchQ(""); loadServers(""); }}
              disabled={serversLoading}
              data-testid="server-nearest-btn"
            >
              📍 الأقرب
            </button>
          </div>

          {serversLoading && (
            <div className="monitor-loading" data-testid="servers-loading">
              <span className="spinner big" /> جاري جلب السيرفرات...
            </div>
          )}

          {!serversLoading && serversError && (
            <div className="monitor-error" data-testid="servers-error">⚠️ {serversError}</div>
          )}

          {!serversLoading && !serversError && servers.length > 0 && (
            <>
              <div className="server-list-hint">
                {serversSource === "search" ? `نتائج البحث (${servers.length})` : `الأقرب لسيرفرك (${servers.length})`} — اضغط لاختيار السيرفر
              </div>
              <div className="server-list" data-testid="server-list">
                {servers.map((s) => (
                  <button
                    key={s.id}
                    className={`server-list-item ${selected?.id === s.id ? "selected" : ""}`}
                    onClick={() => { setSelected(s); setPickerOpen(false); }}
                    data-testid={`server-item-${s.id}`}
                  >
                    <span className="server-item-name">{s.name}</span>
                    <span className="server-item-loc">{s.location}{s.country ? ` — ${s.country}` : ""}</span>
                    <span className="server-item-id" dir="ltr">ID {s.id}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== Live speedometer gauge (car cluster style) ===== */}
      {showGauge && (
        <div className="gauge-wrap" data-testid="speedtest-live-gauge-wrap">
          <SpeedometerGauge value={gaugeValue} phase={phase} />
          <div className="gauge-status">
            <span className={`gauge-phase phase-${phase}`} data-testid="speedtest-phase-label">
              {PHASE_LABELS[phase] || PHASE_LABELS.connecting}
            </span>
            {running && (
              <div className="gauge-progress">
                <div className="gauge-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
            )}
          </div>
          <div className="gauge-live-row">
            <span className="gauge-live-item ping">
              📡 <b dir="ltr" data-testid="live-ping-value">{live?.ping_ms ?? "—"}</b> ms
            </span>
            <span className="gauge-live-item down">
              ⬇️ <b dir="ltr" data-testid="live-download-value">{live?.download_mbps ?? "—"}</b> Mbps
            </span>
            <span className="gauge-live-item up">
              ⬆️ <b dir="ltr" data-testid="live-upload-value">{live?.upload_mbps ?? "—"}</b> Mbps
            </span>
          </div>
        </div>
      )}

      {state?.last_error && !latest && !showGauge && (
        <div className="monitor-error" data-testid="speedtest-error">
          ⚠️ فشل آخر قياس: {state.last_error}
        </div>
      )}

      {!running && !latest && !state?.last_error && !showGauge && (
        <div className="tickets-empty" data-testid="speedtest-empty">
          لم يتم أي قياس بعد — اضغط "ابدأ القياس"
        </div>
      )}

      {latest && !showGauge && (
        <div className="speed-results" data-testid="speedtest-results">
          <div className="speed-metric down">
            <span className="speed-icon">⬇️</span>
            <span className="speed-value" data-testid="speed-download">{latest.download_mbps}</span>
            <span className="speed-unit">Mbps تنزيل</span>
          </div>
          <div className="speed-metric up">
            <span className="speed-icon">⬆️</span>
            <span className="speed-value" data-testid="speed-upload">{latest.upload_mbps}</span>
            <span className="speed-unit">Mbps رفع</span>
          </div>
          <div className="speed-metric ping">
            <span className="speed-icon">📡</span>
            <span className="speed-value" data-testid="speed-ping">{latest.ping_ms}</span>
            <span className="speed-unit">ms بينغ</span>
          </div>
        </div>
      )}

      {latest && !showGauge && (
        <div className="speed-meta" data-testid="speedtest-meta">
          <span>🖥 عبر: {latest.server_name} — {latest.server_location}{latest.server_id ? ` (ID ${latest.server_id})` : ""}</span>
          <span>🕒 {fmtDateTime(latest.created_at)}</span>
          {latest.result_url && (
            <a className="ticket-link" href={latest.result_url} target="_blank" rel="noopener noreferrer" data-testid="speedtest-result-link">
              النتيجة الرسمية ↗
            </a>
          )}
        </div>
      )}

      {history.length > 1 && (
        <div className="speed-history" data-testid="speedtest-history">
          <div className="speed-history-title">آخر القياسات</div>
          <div className="table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>الوقت</th>
                  <th>تنزيل</th>
                  <th>رفع</th>
                  <th>بينغ</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>{fmtDateTime(h.created_at)}</td>
                    <td className="c-ok">{h.download_mbps} Mbps</td>
                    <td>{h.upload_mbps} Mbps</td>
                    <td>{h.ping_ms} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
